import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Square } from "lucide-react";

interface VoiceSearchButtonProps {
  onResult: (text: string) => void;
  lang: "ar" | "en";
  className?: string;
  size?: "sm" | "md";
}

export function VoiceSearchButton({ onResult, lang, className = "", size = "md" }: VoiceSearchButtonProps) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState<boolean | null>(null);
  const recRef = useRef<any>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    setSupported(true);
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = lang === "ar" ? "ar-SA" : "en-US";

    rec.onresult = (e: any) => {
      let final = "";
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final.trim()) onResultRef.current(final.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;

    return () => { try { rec.abort(); } catch {} };
  }, [lang]);

  const toggle = useCallback(() => {
    if (!recRef.current) return;
    if (listening) {
      recRef.current.stop();
      setListening(false);
    } else {
      try {
        recRef.current.start();
        setListening(true);
      } catch {}
    }
  }, [listening]);

  if (supported === false) return null;

  const iconSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
  const btnSize = size === "sm" ? "p-2" : "p-2.5";

  return (
    <button
      type="button"
      onClick={toggle}
      className={`${btnSize} rounded-xl transition-all shrink-0 flex items-center justify-center border ${
        listening
          ? "bg-red-500 text-white animate-pulse shadow-md border-red-500"
          : "bg-gradient-to-b from-[#0E2A52] to-[#08183A] border-blue-400/30 text-blue-200 hover:from-[#0E2A52]/90 hover:to-[#08183A]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]"
      } ${className}`}
      title={lang === "ar" ? (listening ? "إيقاف التسجيل" : "بحث صوتي") : listening ? "Stop recording" : "Voice search"}
    >
      {listening ? <Square className={iconSize} /> : <Mic className={iconSize} />}
    </button>
  );
}
